// CompanyPage.js
import React, { useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { BoxArrowUpRight } from 'react-bootstrap-icons';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useApi2Resource from '../../hooks/useApi2Resource';
import { Bars, FeedButton, FeedCard, FeedSection, PanelState } from '../feed/FeedCard';
import { openExternal } from '../../utils/external';
import {
  companyRecordUrl,
  fetchCompany,
  fetchRecipientAwards,
  fetchTradesForTicker,
  formatCount,
  formatDay,
  formatDollars,
  formatPercent,
  formatScore,
  titleCaseName,
} from '../../utils/spending';
import TradeRow from './TradeRow';

import '../members/MemberDetailPage.css';
import './Spending.css';

/**
 * CompanyPage
 *
 * One company, mounted at /us/company/:orgId, from /influence/companies/{id}:
 * everything the records join to it, each block shown only when its record
 * holds something, so a private company's page does not fill up with empty
 * SEC figures.
 *
 * The order runs from what the government pays it to what it spends to be
 * heard, and the two are never combined:
 *
 *   indicators   dependence, reliance and influence activity, each explained
 *   paid         contracts, by agency and by payee, and the newest awards
 *   other money  subcontracts, grants and loans, each on its own line
 *   heard        lobbying (in-house and outside firms apart), its PAC's
 *                giving, and its own treasury money to super PACs, apart
 *   trades       members' trades in its shares -- the members', not its own
 *   other        comments on federal rules, exclusions, what it reports to
 *                the SEC
 *
 * Fields inside `detail` are read defensively: each block renders from what
 * is there and leaves out what is not, and the full record on api2 is one
 * tap away for anything this page does not show.
 */

const isNum = (value) => typeof value === 'number' && !Number.isNaN(value);

// A figure may arrive bare or as { total | amount | obligations }.
const amountOf = (value) => {
  if (isNum(value)) return value;
  if (value && typeof value === 'object') {
    return [value.total, value.amount, value.obligations, value.obligated].find(isNum) ?? null;
  }
  return null;
};

const Indicator = ({ label, value, note }) => (
  <div className="company-indicator">
    <div className="company-indicator-value">{value}</div>
    <div className="company-indicator-label">{label}</div>
    {note && <div className="company-indicator-note">{note}</div>}
  </div>
);

const Fact = ({ label, value, note }) =>
  value && value !== '\u2014' ? (
    <div className="company-fact">
      <div className="company-fact-row">
        <span>{label}</span>
        <span className="company-fact-value">{value}</span>
      </div>
      {note && <div className="company-fact-note">{note}</div>}
    </div>
  ) : null;

function CompanyPage() {
  const { t } = useTranslation('common');
  const { orgId } = useParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [orgId]);

  const company = useApi2Resource(() => fetchCompany(orgId), [orgId]);
  const data = company.data || {};
  const detail = data.detail || {};

  const tickers = Array.isArray(data.tickers) ? data.tickers : [];
  const payees = Array.isArray(detail.contracts?.recipients) ? detail.contracts.recipients : [];
  const agencies = Array.isArray(detail.contracts?.agencies) ? detail.contracts.agencies : [];
  const firstPayee = payees.find((payee) => payee.recipient_id) || null;

  const awards = useApi2Resource(
    firstPayee ? () => fetchRecipientAwards(firstPayee.recipient_id) : null,
    [firstPayee?.recipient_id]
  );
  const trades = useApi2Resource(tickers[0] ? () => fetchTradesForTicker(tickers[0]) : null, [
    tickers[0],
  ]);

  if (company.loading || company.error || !company.data) {
    return (
      <div id="feedDisplay" className="member-detail-page">
        <div className="member-page-content spending-page">
          {company.loading ? (
            <div className="spending-page-loading">
              <Spinner id="spinner" animation="grow" variant="light" />
            </div>
          ) : (
            <FeedCard>
              <p className="spending-lead">
                {company.error?.notFound
                  ? t('common:companyNotFound', 'No company in the dataset has that id.')
                  : company.error?.detail ||
                    t('common:companyLoadError', 'This company could not be loaded right now.')}
              </p>
            </FeedCard>
          )}
        </div>
      </div>
    );
  }

  const name = titleCaseName(data.name);

  // A null dependence ratio has one of three reasons, and saying which is
  // the difference between "unknown" and "none".
  const dependenceNote = () => {
    if (isNum(data.dependence_ratio)) {
      return t(
        'common:dependenceNote',
        'Federal contracts as a share of revenue. Can exceed 100%, because obligations are paid over years.'
      );
    }
    if (!isNum(data.contracts) || data.contracts === 0) {
      return t('common:dependenceNoContracts', 'No federal contracts recorded.');
    }
    if (data.ownership === 'public') {
      return t('common:dependenceNoRevenue', 'No dollar revenue on file with the SEC.');
    }
    return t(
      'common:dependencePrivate',
      'A private company reports no revenue to a federal agency, so this cannot be computed.'
    );
  };

  const lobbyingSpending = detail.lobbying?.spending || {};
  const inHouse = amountOf(lobbyingSpending.in_house);
  const outsideFirms = amountOf(lobbyingSpending.outside_firms);

  const other = detail.other_awards || {};
  const subcontracts = amountOf(other.subcontracts);
  const grants = amountOf(other.grants);
  const loans = amountOf(other.loans);

  const exclusions = Array.isArray(detail.exclusions) ? detail.exclusions : [];
  const sec = detail.sec || null;
  const newestAwards = Array.isArray(awards.data?.results) ? awards.data.results : [];
  const tradeRows = Array.isArray(trades.data?.results) ? trades.data.results : [];

  const hasHeard =
    isNum(data.lobbying) || isNum(inHouse) || isNum(outsideFirms) || isNum(data.pac_total) || isNum(data.treasury_political);

  return (
    <div id="feedDisplay" className="member-detail-page">
      <div className="member-page-content spending-page">
        {/* ─── Who ─── */}
        <FeedCard
          className="spending-hero"
          title={name}
          footer={
            <>
              <FeedButton primary onClick={() => openExternal(companyRecordUrl(orgId))}>
                <BoxArrowUpRight size={12} className="me-1" aria-hidden />
                {t('common:fullRecord', 'Full record')}
              </FeedButton>
              {sec?.url && (
                <FeedButton onClick={() => openExternal(sec.url)}>
                  <BoxArrowUpRight size={12} className="me-1" aria-hidden />
                  {t('common:secFilings', 'SEC filings')}
                </FeedButton>
              )}
            </>
          }
        >
          <div className="company-tags">
            {data.ownership && (
              <span className="company-tag">
                {data.ownership === 'public'
                  ? t('common:publiclyTraded', 'Publicly traded')
                  : t('common:notListed', 'Not listed')}
              </span>
            )}
            {tickers.map((ticker) => (
              <span key={ticker} className="company-tag company-tag--ticker">
                {ticker}
              </span>
            ))}
          </div>
        </FeedCard>

        {/* ─── Indicators ─── */}
        <FeedSection
          title={t('common:indicators', 'Indicators')}
          subtitle={t(
            'common:indicatorsSubtitle',
            'Both directions, with equal weight: how much it relies on the government, and how much the government relies on it.'
          )}
        >
          <FeedCard>
            <div className="company-indicators">
              <Indicator
                label={t('common:dependence', 'Dependence on federal contracts')}
                value={formatPercent(data.dependence_ratio)}
                note={dependenceNote()}
              />
              <Indicator
                label={t('common:relianceScore', 'Government reliance score')}
                value={formatScore(data.reliance_score)}
                note={t('common:outOf100', 'Out of 100.')}
              />
              <Indicator
                label={t('common:influenceScore', 'Influence activity score')}
                value={formatScore(data.influence_score)}
                note={t(
                  'common:influenceScoreNote',
                  'Ranks disclosed lobbying, lobbyists and PAC giving. It does not say whether any of it worked.'
                )}
              />
            </div>
          </FeedCard>
        </FeedSection>

        {/* ─── What the government pays it ─── */}
        {(isNum(data.contracts) || agencies.length > 0 || payees.length > 0) && (
          <FeedSection
            title={t('common:whatGovernmentPays', 'What the government pays it')}
            subtitle={
              isNum(data.contracts)
                ? t('common:contractsObligated', '{{amount}} in federal contract obligations', {
                    amount: formatDollars(data.contracts),
                  })
                : null
            }
          >
            <FeedCard>
              {agencies.length > 0 && (
                <div className="spending-list">
                  <h3 className="spending-list-title">{t('common:byAgency', 'By agency')}</h3>
                  <Bars
                    rows={agencies}
                    tone="gov"
                    share={() => null}
                    format={(value) => formatDollars(value)}
                    sub={(row) =>
                      isNum(row.share_of_agency)
                        ? t('common:shareOfAgency', '{{share}} of the agency\u2019s contracts', {
                            share: formatPercent(row.share_of_agency),
                          })
                        : null
                    }
                  />
                </div>
              )}

              {payees.length > 0 && (
                <div className="spending-list">
                  <h3 className="spending-list-title">
                    {t('common:payees', 'Paid as')}
                  </h3>
                  <p className="spending-hint">
                    {t(
                      'common:payeesNote',
                      'A parent company and each of its subsidiaries are separate recipients, each with its own UEI.'
                    )}
                  </p>
                  <ul className="company-payees">
                    {payees.map((payee, index) => (
                      <li key={payee.recipient_id || `${payee.name}-${index}`}>
                        <span>
                          {payee.url ? (
                            <span
                              className="trade-link"
                              role="link"
                              tabIndex={0}
                              onClick={() => openExternal(payee.url)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') openExternal(payee.url);
                              }}
                            >
                              {titleCaseName(payee.name)}
                            </span>
                          ) : (
                            titleCaseName(payee.name)
                          )}
                          {payee.uei && <span className="company-uei">UEI {payee.uei}</span>}
                        </span>
                        {isNum(amountOf(payee.amount ?? payee)) && (
                          <span className="company-fact-value">{formatDollars(amountOf(payee.amount ?? payee))}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {firstPayee && (
                <div className="spending-list">
                  <h3 className="spending-list-title">
                    {t('common:newestAwardsTo', 'Newest awards to {{name}}', {
                      name: titleCaseName(firstPayee.name),
                    })}
                  </h3>
                  <PanelState
                    loading={awards.loading}
                    error={awards.error}
                    empty={!awards.loading && !awards.error && !newestAwards.length}
                    emptyText={t('common:noAwardsYear', 'No awards first obligated in the last year.')}
                  />
                  <ul className="company-awards">
                    {newestAwards.map((award, index) => (
                      <li key={award.generated_internal_id || index}>
                        <div className="company-fact-row">
                          <span>{award['Awarding Agency']}</span>
                          <span className="company-fact-value">
                            {formatDollars(award['Award Amount'] ?? award['Loan Value'])}
                          </span>
                        </div>
                        {award.Description && <div className="award-description">{award.Description}</div>}
                        {formatDay(award['Base Obligation Date']) && (
                          <div className="award-meta">
                            {t('common:firstObligated', 'first obligated {{date}}', {
                              date: formatDay(award['Base Obligation Date']),
                            })}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </FeedCard>
          </FeedSection>
        )}

        {/* ─── Other federal money ─── */}
        {(isNum(subcontracts) || isNum(grants) || isNum(loans)) && (
          <FeedSection
            title={t('common:otherFederalMoney', 'Other federal money')}
            subtitle={t(
              'common:otherFederalMoneyNote',
              'Each on its own line and never added to contracts. Subcontracts are already inside a prime contractor\u2019s obligations.'
            )}
          >
            <FeedCard>
              <Fact label={t('common:subcontracts', 'Subcontracts')} value={formatDollars(subcontracts)} />
              <Fact label={t('common:grants', 'Grants')} value={formatDollars(grants)} />
              <Fact
                label={t('common:loansFaceValue', 'Loans (face value)')}
                value={formatDollars(loans)}
              />
            </FeedCard>
          </FeedSection>
        )}

        {/* ─── What it spends to be heard ─── */}
        {hasHeard && (
          <FeedSection
            title={t('common:toBeHeard', 'What it spends to be heard')}
            subtitle={t(
              'common:toBeHeardNote',
              'Separate records filed under separate laws. None of these is added to another.'
            )}
          >
            <FeedCard>
              <Fact
                label={t('common:reportedLobbying', 'Reported lobbying')}
                value={formatDollars(data.lobbying)}
                note={
                  isNum(data.lobbyists)
                    ? t('common:lobbyistCount', '{{count}} lobbyists', { count: formatCount(data.lobbyists) })
                    : null
                }
              />
              <Fact label={t('common:lobbyingInHouse', 'Lobbying in-house')} value={formatDollars(inHouse)} />
              <Fact
                label={t('common:lobbyingOutside', 'Lobbying by outside firms')}
                value={formatDollars(outsideFirms)}
              />
              <Fact
                label={t('common:pacGave', 'Its PAC gave')}
                value={formatDollars(data.pac_total)}
                note={t('common:pacNotCompany', 'The PAC\u2019s money, not the company\u2019s.')}
              />
              <Fact
                label={t('common:treasuryPolitical', 'From its own funds, to super PACs')}
                value={formatDollars(data.treasury_political)}
                note={t('common:treasuryNote', 'Gifts to super PACs and hybrid PACs. Not PAC money.')}
              />
            </FeedCard>
          </FeedSection>
        )}

        {/* ─── Members' trades ─── */}
        {tickers[0] && (isNum(data.congress_trades) ? data.congress_trades > 0 : true) && (
          <FeedSection
            title={t('common:membersTrades', 'Members\u2019 trades in its shares')}
            subtitle={
              isNum(data.congress_trades)
                ? t(
                    'common:membersTradesCount',
                    '{{count}} reported transactions. The members\u2019 trades, not the company\u2019s.',
                    { count: formatCount(data.congress_trades) }
                  )
                : t('common:membersTradesNote', 'The members\u2019 trades, not the company\u2019s.')
            }
          >
            <FeedCard>
              <PanelState
                loading={trades.loading}
                error={trades.error}
                empty={!trades.loading && !trades.error && !tradeRows.length}
              />
              {tradeRows.length > 0 && (
                <ul className="trade-list">
                  {tradeRows.map((trade, index) => (
                    <TradeRow key={`${trade.url}-${index}`} trade={trade} />
                  ))}
                </ul>
              )}
            </FeedCard>
          </FeedSection>
        )}

        {/* ─── Other records ─── */}
        {(isNum(data.reg_comments) || exclusions.length > 0 || sec) && (
          <FeedSection title={t('common:otherRecords', 'Other records')}>
            <FeedCard>
              <Fact
                label={t('common:ruleComments', 'Comments on federal rules')}
                value={isNum(data.reg_comments) ? formatCount(data.reg_comments) : null}
                note={t(
                  'common:ruleCommentsNote',
                  'A floor: a comment filed under a generic title is not counted.'
                )}
              />
              {sec && (
                <Fact
                  label={
                    sec.fiscal_year
                      ? t('common:secRevenueYear', 'Revenue reported to the SEC, FY{{year}}', {
                          year: sec.fiscal_year,
                        })
                      : t('common:secRevenue', 'Revenue reported to the SEC')
                  }
                  value={formatDollars(amountOf(sec.revenue))}
                />
              )}
              {exclusions.length > 0 && (
                <div className="spending-list">
                  <h3 className="spending-list-title">
                    {t('common:activeExclusions', 'Active SAM.gov exclusions')}
                  </h3>
                  <ul className="company-payees">
                    {exclusions.map((exclusion, index) => (
                      <li key={index}>
                        <span>
                          {[exclusion.agency, exclusion.type || exclusion.exclusion_type]
                            .filter(Boolean)
                            .join(', ')}
                        </span>
                        <span className="company-fact-value">
                          {formatDay(exclusion.date || exclusion.active_date) || ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </FeedCard>
          </FeedSection>
        )}

        <p className="spending-provenance">
          {t(
            'common:companyProvenance',
            'Joined weekly from USAspending, the SEC, Senate lobbying filings and the FEC. A tie between records shows a record exists, not that anything was exchanged.'
          )}
        </p>
      </div>

      <style>{`
        .member-page-content {
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
          box-sizing: border-box;
        }
      `}</style>
    </div>
  );
}

export default CompanyPage;